import { Field, Form, Formik, FormikHelpers } from "formik";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import * as Yup from "yup";
import { CustomFieldWithError } from "../../components/forms/CustomFieldWithError";
import { useTranslation } from "react-i18next";
import Button from "../../components/button";
import { ButtonSize, ButtonVariant } from "../../components/button/types";
import { fetch360Feedback, submitFeedback } from "../../services/feedback360.service";
import { CookieConsentBanner } from "../private/legal/CookieConsent";
import { useCookieConsentBanner } from "../../hooks/useCookieConsentBanner";

export interface Feedback360SubmitData extends Feedback360FormValues {
    uuid: string;
  }
  
export interface Feedback360FormValues {
  collaboration_rating: number;
  collaboration_positive: string;
  strength: string;
  collaboration_negative: string;
  weakness: string;
  future_role: string;
}

export interface Feedback360Response {
  isValid: boolean;
  isSubmitted: boolean;
  isOver: boolean;
  user?: {
    firstName: string;
    lastName: string;
  };
  [key: string]: any;
}

// Validation schema for the form
const validationSchema = Yup.object().shape({
  collaboration_rating: Yup.number()
    .min(1, "Rating is required")
    .required("Rating is required"),
  collaboration_positive: Yup.string().required("This field is required"),
  strength: Yup.string().required("This field is required"),
  collaboration_negative: Yup.string().required("This field is required"),
  weakness: Yup.string().required("This field is required"),
  future_role: Yup.string().required("This field is required"),
});



export default function PublicFeedback360(): JSX.Element {
  const uuid = new URLSearchParams(window.location.search).get("uuid");
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false);
  const { t } = useTranslation("feedback360");
  const [message, setMessage] = useState<string | null>(null);
  const [displayCookieConsent, setDisplayCookieConsent] = useState(true);
  useCookieConsentBanner({
    displayCookieConsent: () => setDisplayCookieConsent(true),
    hideCookieConsent: () => setDisplayCookieConsent(false),
  });
  // Query to validate the UUID and check if feedback has already been submitted
  const { isLoading, isError, data } = useQuery<Feedback360Response, Error>(
    ["feedback", uuid],
    () => {
      if (!uuid) {
        throw new Error("Invalid uuid");
      }
      return fetch360Feedback(uuid);
    },
    {
      enabled: !!uuid,
      onError: (error: Error) => {
        setMessage(t("invalid-request"));
      },
    }
  );

  // Mutation to submit feedback
  const feedbackMutation = useMutation<any, Error, Feedback360FormValues>(
    (values: Feedback360FormValues) =>
      submitFeedback({ ...values, uuid: uuid || "" }),
    {
      onSuccess: () => {
        setMessage(t("submit-success"));
        setFeedbackSent(true);
      },
      onError: () => {
        setMessage(t("submit-error"));
      },
    }
  );

  // Initial form values
  const initialValues: Feedback360FormValues = {
    collaboration_rating: 0,
    collaboration_positive: "",
    strength: "",
    collaboration_negative: "",
    weakness: "",
    future_role: "",
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full p-9 text-center">
        <p>{t("loading")}</p>
      </div>
    );
  }

  // Show error message if the uuid is invalid
  if (data && !data.isValid) {
    return (
      <div className="w-full p-9 text-center">
        <p>{t("invalid-request")}</p>
      </div>
    );
  }



  // Show thank you message if feedback was already sent or just submitted
  if (feedbackSent || (data && data.isSubmitted)) {
    return (
      <div className="w-full p-9 text-center">
        <h2 className="text-2xl font-bold mb-4">
          {t("thank-you-title")}
        </h2>
        <p>{t("thank-you-message")}</p>
      </div>
    );
  }

  if (data && data.isOver) {
    return (
      <div className="w-full p-9 text-center">
      <h2 className="text-2xl font-bold mb-4">
        {t("thank-you-title")}
      </h2>
      <p>{t("over")}</p>
    </div>
    );
  }

  // Show error message if the query failed
  if (isError) {
    return (
      <div className="w-full p-9 text-center">
        <p>{message || t("invalid-request")}</p>
      </div>
    );
  }

  return (
    <div className="w-full p-9">
      <h1 className="text-2xl font-bold mb-8">{t("title")}</h1>
      <div className="mb-6">{t("description")}</div>
      <CookieConsentBanner
              showBanner={displayCookieConsent}
              closeBanner={() => setDisplayCookieConsent(false)}
            />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(
          values: Feedback360FormValues,
          actions: FormikHelpers<Feedback360FormValues>
        ) => {
          feedbackMutation.mutate(values);
        }}
      >
        {({ errors, touched, isValid, values, setFieldValue }) => {
          // Check if all required fields are filled
          const allRequiredFieldsFilled = Object.keys(initialValues).every(
            (key) => {
              // Use type assertion to tell TypeScript this is a valid key
              const fieldKey = key as keyof Feedback360FormValues;

              if (fieldKey === "collaboration_rating") {
                return values[fieldKey] > 0;
              }
              return Boolean(values[fieldKey]);
            }
          );

          const disabled =
            !isValid || !allRequiredFieldsFilled || feedbackMutation.isLoading;

          return (
            <Form className="space-y-6">
              {/* Star Rating */}
              <div className="w-full">
                <label className="block mb-2 font-medium">
                  {t("collaboration_rating")}*
                </label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() =>
                        setFieldValue("collaboration_rating", star)
                      }
                      className={`w-10 h-10 flex items-center justify-center rounded-full focus:outline-none ${
                        values.collaboration_rating >= star
                          ? "text-yellow-400"
                          : "text-gray-300"
                      }`}
                      aria-label={`Rate ${star} out of 5 stars`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-8 h-8"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  ))}
                </div>
                {errors.collaboration_rating &&
                  touched.collaboration_rating && (
                    <div className="text-red-500 mt-1">
                      {errors.collaboration_rating}
                    </div>
                  )}
              </div>

              {/* Positive Collaboration */}
              <div className="w-full">
                <label
                  htmlFor="collaboration_positive"
                  className="block mb-2 font-medium"
                >
                  {t("collaboration_positive")}*
                </label>
                <Field
                  as="textarea"
                  className={`border px-4 rounded w-full h-32 p-2 mt-1 ${
                    errors.collaboration_positive &&
                    touched.collaboration_positive
                      ? "border-red-500"
                      : ""
                  }`}
                  id="collaboration_positive"
                  name="collaboration_positive"
                  placeholder= {t("placeholder")}
                />
                {errors.collaboration_positive &&
                touched.collaboration_positive ? (
                  <div className="text-sm text-red-500">
                    {errors.collaboration_positive}
                  </div>
                ) : null}
              </div>

              {/* Strengths */}
              <div className="w-full">
                <label htmlFor="strength" className="block mb-2 font-medium">
                  {t("strength")}*
                </label>
                <Field
                  as="textarea"
                  className={`border px-4 rounded w-full h-32 p-2 mt-1 ${
                    errors.strength && touched.strength ? "border-red-500" : ""
                  }`}
                  id="strength"
                  name="strength"
                  placeholder= {t("placeholder")}
                />
                {errors.strength && touched.strength ? (
                  <div className="text-sm text-red-500">{errors.strength}</div>
                ) : null}
              </div>

              {/* Negative Collaboration */}
              <div className="w-full">
                <label
                  htmlFor="collaboration_negative"
                  className="block mb-2 font-medium"
                >
                  {t("collaboration_negative")}*
                </label>
                <Field
                  as="textarea"
                  className={`border px-4 rounded w-full h-32 p-2 mt-1 ${
                    errors.collaboration_negative &&
                    touched.collaboration_negative
                      ? "border-red-500"
                      : ""
                  }`}
                  id="collaboration_negative"
                  name="collaboration_negative"
                  placeholder= {t("placeholder")}
                />
                {errors.collaboration_negative &&
                touched.collaboration_negative ? (
                  <div className="text-sm text-red-500">
                    {errors.collaboration_negative}
                  </div>
                ) : null}
              </div>

              {/* Weaknesses */}
              <div className="w-full">
                <label htmlFor="weakness" className="block mb-2 font-medium">
                  {t("weakness")}*
                </label>
                <Field
                  as="textarea"
                  className={`border px-4 rounded w-full h-32 p-2 mt-1 ${
                    errors.weakness && touched.weakness ? "border-red-500" : ""
                  }`}
                  id="weakness"
                  name="weakness"
                  placeholder= {t("placeholder")}
                />
                {errors.weakness && touched.weakness ? (
                  <div className="text-sm text-red-500">{errors.weakness}</div>
                ) : null}
              </div>

              {/* Future Role */}
              <div className="w-full">
                <label htmlFor="future_role" className="block mb-2 font-medium">
                  {t("future_role")}*
                </label>
                <Field
                  as="textarea"
                  className={`border px-4 rounded w-full h-32 p-2 mt-1 ${
                    errors.future_role && touched.future_role
                      ? "border-red-500"
                      : ""
                  }`}
                  id="future_role"
                  name="future_role"
                  placeholder= {t("placeholder")}
                />
                {errors.future_role && touched.future_role ? (
                  <div className="text-sm text-red-500">
                    {errors.future_role}
                  </div>
                ) : null}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="mt-6 w-full"
                disabled={disabled}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.MEDIUM}
              >
                {feedbackMutation.isLoading
                  ? t("buttons.submitting")
                  : t("buttons.submit_feedback")}
              </Button>
            </Form>
          );
        }}
      </Formik>

      {message && !feedbackSent && (
        <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
          {message}
        </div>
      )}
    </div>
  );
}